import './BOShippingInfo.scss';

import Proptypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import AdditionalFullFilmentOption from '@components/Share/AdditionalFullFilmentOption';
import ModalComponent from '@components/Share/ModalComponent';
import SFExpressPopup from '@components/SlicingPage/CartDetails/SFExpressPopup';
import SitecoreContextFactoryService from '@services/sitecoreContextFactoryService';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import { ASSEMBLE_OPTION_KEY, DELIVERY_OPTION } from '@utils/constant';
import { useDidUpdateEffect } from '@utils/customsHook/useDidUpdateEffect';
import useIsEnableBOPIS from '@utils/customsHook/useIsEnableBOPIS';

import BOShippingInfoLayout from './BOShippingInfoLayout';
import { useBOGetAvailabilityStores } from './hooks';
import PickupFromStore from './PickupFromStore';
import { useCheckBOPISAvailable } from './PickupFromStore/hooks';

const BOShippingInfo = (props) => {
  const {
    deliveryOptions,
    deliveryStatus,
    statusLBL,
    dataTextFields
  } = props;
  const isEnableBOPIS = useIsEnableBOPIS();
  const checkBOPISAvailabilityService = useCheckBOPISAvailable();
  const getAvailabilityStoresService = useBOGetAvailabilityStores();
  const [isShowSFExpress, setShowSFExpress] = useState(false);
  const isClientTelingUser = SitecoreContextFactoryService.getValueContextItem('isClientTelingUser');
  const boVariantsReducer = useSelector((state) => state.buyingOptionReducer.BOVariantsReducer);

  const freeGiftsSelectedID = useSelector(state => state.buyingOptionReducer.BOFreeGiftsReducer.freeGiftsSelectedID)
    || [];

  const additionalOptionsActive = useSelector(state => state.buyingOptionReducer.BOAdditionalOptionsReducer.additionalOptionsActive);

  const allowedOptions = useMemo(() => (
    dataTextFields?.['Additional Options Setting']?.fields?.['Allowed Options'] || []
  ), [dataTextFields]);

  const isRequiredAssemblyOption = useMemo(() => {
    return allowedOptions?.find((optionItem) => optionItem?.fields?.['Relationship']?.fields?.['Field Name']?.value
      === ASSEMBLE_OPTION_KEY)?.fields?.Required;
  }, [allowedOptions]);

  const { isEnableBOPISOption } = checkBOPISAvailabilityService(isRequiredAssemblyOption);

  const handleGetAvailabilityStores = useCallback(async () => {
    const mappingAllowedOptionsData = allowedOptions?.map((optionItem) => (
      {
        required: optionItem?.fields?.Required,
        optionId: optionItem?.fields?.Relationship?.fields?.['Field Name']?.value
      }
    ));

    return await getAvailabilityStoresService(mappingAllowedOptionsData);
  }, [allowedOptions, getAvailabilityStoresService]);

  useEffect(() => {
    if (isEnableBOPISOption) {
      (
        async () => await handleGetAvailabilityStores()
      )();
    }
  }, [isEnableBOPISOption]);

  useDidUpdateEffect(
    () => {
      /** INFO: re-fetch store available with selected variant */
      if (isEnableBOPISOption && (
        boVariantsReducer?.variantSelectedLength || Object.keys(additionalOptionsActive).length
      )) {
        (
          async () => await handleGetAvailabilityStores()
        )();
      }
    },
    [
      isEnableBOPISOption,
      boVariantsReducer?.variantSelectedLength,
      JSON.stringify(boVariantsReducer?.variantActiveID),
      JSON.stringify(freeGiftsSelectedID),
      JSON.stringify(additionalOptionsActive)
    ]
  );

  const onCloseModalHandler = () => {
    setShowSFExpress(false);
  };

  return (
    <div className={`${isClientTelingUser ? 'col-xl-6 shipping-info-cp' : 'shipping-info'}`}>
      <div className='shipping-info__wrap'>
        {deliveryOptions?.length ? deliveryOptions.map((deliveryItem) => {
          const shippingOptionType = deliveryItem.ShippingOptionType.Value;

          return (
            DELIVERY_OPTION.PickUpStore
            !== shippingOptionType
            || DELIVERY_OPTION.PickUpStore
            === shippingOptionType
            && !isEnableBOPIS
          ) ? (
              <BOShippingInfoLayout
                key={deliveryItem.ExternalId} name={deliveryItem.Name}
                shippingOptionType={shippingOptionType}
              >
                {DELIVERY_OPTION.SFExpress === shippingOptionType ? (
                  <Fragment>
                    <span className='shipping-info__item__text__desc'>
                      <Text className='smaller-text' field={dataTextFields['Available Label']} tag='span' />
                      <span className='shipping-info__item__text__desc'>
                        {/* <Text className='smaller-text--inline smaller-text--underline' onClick={() => setShowSFExpress(true)} tag='span' field={dataTextFields['SF Express Station Label']} /> */}
                        <RichText field={dataTextFields['SF Express Station Label']} />
                      </span>
                    </span>
                  </Fragment>
                ) : (
                  <></>
                )}

                {DELIVERY_OPTION.ShipItem === shippingOptionType ? (
                  <Fragment>
                    <span
                      className='shipping-info__item__text__desc'
                    >{statusLBL}{!deliveryStatus ? '' : '. '}{deliveryStatus}</span>
                    <AdditionalFullFilmentOption
                      inputData={dataTextFields['Additional Fulfillment Info']}
                    />
                  </Fragment>
                ) : (
                  <></>
                )}

                {DELIVERY_OPTION.PickUpStore === deliveryItem.ShippingOptionType.Value ?
                  <span className='shipping-info__item__text__desc link-store'>3 Stores</span> : null}

                {DELIVERY_OPTION.DeliveryBySFExpress === shippingOptionType ? (
                  <Fragment>
                    <span className='shipping-info__item__text__desc'>
                      <span className='smaller-text'>{deliveryItem?.Description}</span>
                    </span>
                  </Fragment>
                ) : (
                  <></>
                )}
              </BOShippingInfoLayout>
            ) : (
              isEnableBOPIS ? (
                <PickupFromStore
                  key={deliveryItem.ExternalId}
                  name={deliveryItem.Name}
                  addressSetting={dataTextFields?.['Address Settings']}
                />
              ) : (
                <></>
              )
            );
        }) : (
          <></>
        )}
      </div>
      <ModalComponent
        isShow={isShowSFExpress} onCloseHandler={onCloseModalHandler}
        titlePopup={<Text field={dataTextFields['SF Express Popup Title']} />}
      >
        <SFExpressPopup dataSources={dataTextFields} />
      </ModalComponent>
    </div>
  );
};

BOShippingInfo.propTypes = {
  deliveryOptions: Proptypes.array,
  deliveryStatus: Proptypes.any,
  statusLBL: Proptypes.any,
  dataTextFields: Proptypes.any
};

export default BOShippingInfo;
